// src/Components/LearningPath/LearningPathSequence/LearningPathSequence.jsx
import React, { useMemo } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { SortableItem } from '../../DragabbleAndSortable/SortableItem';
import { PathDocumentItem } from '../PathDocumentItem';
import { i18n } from "../../../Utils/i18n/i18n";
import { SignDocTypes } from "../../SignDoc/SignDocLib";
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import './LearningPathSequence.scss';
import {getSequenceOrderedDocuments} from "../../../Utils/LearningPath/LearningPathGraph";
import {BEFORE_FIRST_DROP_ZONE_ID, BETWEEN_DROP_ZONE_PREFIX} from "../LearningPathLib";
import {map} from "lodash";

// Separate component for intermediate drop zones that look like arrows
const IntermediateDropZone = ({ prevDocId, nextDocId, isBeforeFirst = false }) => {
    const dropId = isBeforeFirst ?
        `drop-zone-${BEFORE_FIRST_DROP_ZONE_ID}` :
        `drop-zone-${BETWEEN_DROP_ZONE_PREFIX}${prevDocId}:${nextDocId}`;

    const { setNodeRef, isOver } = useDroppable({
        id: dropId,
    });

    return (
        <div className={`arrowDropZone ${isOver ? 'over' : ''}`} ref={setNodeRef}>
            {!isBeforeFirst ? <ArrowDownwardIcon className="arrowIcon" aria-hidden="true" /> : ''}
        </div>
    );
};

export const LearningPathSequence = ({graph, onRemoveDocument, allDocuments }) => {
    const { setNodeRef: mainDropRef, isOver: isMainOver } = useDroppable({
        id: 'drop-zone',
    });

    const documents = useMemo(() => {
        return graph && graph.rootId ? getSequenceOrderedDocuments({ graph, allDocuments }) : [];
    }, [graph, allDocuments]);

    return (
        <div className="LearningPathSequence">
            {documents.length > 0 ? (
                <SortableContext
                    items={map(documents, doc => doc.id)}
                    strategy={verticalListSortingStrategy}
                >
                    <div className="sequenceFlow">
                        <IntermediateDropZone isBeforeFirst={true} />
                        {map(documents, (document, index) => (
                            <React.Fragment key={document.id}>
                                {index > 0 && (
                                    <IntermediateDropZone
                                        prevDocId={documents[index-1].id}
                                        nextDocId={document.id}
                                    />
                                )}
                                <div className="sequenceItem">
                                    <SortableItem id={document.id}>
                                        <div className="flowNode">
                                            <div className="hexNode">
                                                <div className="nodeNumber">{index + 1}</div>
                                            </div>
                                            <div className="documentContainer">
                                                <PathDocumentItem
                                                    document={document}
                                                    onRemove={onRemoveDocument}
                                                />
                                                {document.type === SignDocTypes.EXAM && (
                                                    <div className="completionRequirements">
                                        <span className="requirementLabel">
                                            {i18n.translate('learningPath.passingGradeRequired')}
                                        </span>
                                                        <span className="requirementValue">
                                            {document.questionnaire?.passGrade || 0}
                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </SortableItem>
                                </div>
                            </React.Fragment>
                        ))}
                    </div>
                </SortableContext>
            ) : (
                <div className="emptyMessage">
                    {i18n.translate('learningPath.noDocumentsSelected')}
                </div>
            )}

            <div className={`mainDropZone ${isMainOver ? 'over' : ''}`} ref={mainDropRef}>
                <div className="plusIcon" aria-hidden="true">+</div>
                <div className="dropText">{i18n.translate('learningPath.dropHere')}</div>
            </div>
        </div>
    );
};